"use client";

import { useMemo, useState, useEffect } from "react";
import { Box, Tab, Tabs, Typography, useMediaQuery } from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import {
  allYears,
  clauseRows,
  creditorTypeColumns,
  denominationYearRows,
  denominationColumns,
  flowRows,
  governingLawColumns,
  governingLawYearRows,
  gtcColumns,
  gtcYearRows,
  instrumentColumns,
  instrumentYearRows,
  maxYear,
  minYear,
} from "./data";
import temporalDataJson from "@/_data/dashboard/temporal_data_092826.json"
import type { AnalyticsFilters, YearlyRow } from "./types";
import { isCompleteDataFalse, parseNode } from "./utils";
import CreditFlowsTab from "./tabs/CreditFlowsTab";
import LoanCharacteristicsTab, {
  type ExpandedLoanSections,
} from "./tabs/LoanCharacteristicsTab";
import PartyCharacteristicsTab from "./tabs/PartyCharacteristicsTab";
import UserStatisticsTab from "./tabs/UserStatisticsTab";
import { useUserStatsData } from "./tabs/UserStatisticsTab";
import ClauseTab from "./tabs/ClauseTab";
import ClauseDeepDiveTab from "./tabs/ClauseDeepDiveTab";
import ProvenanceTab from "./tabs/ProvenanceTab";
import AnalyticsMetricCards from "./AnalyticsMetricCards";

type TemporalDataRow = {
  DocumentYear: number;
  BorrowerGeographicRegion: string;
  CreditorGeographicRegion: string;
  pCreditorType: string;
  inferred_denominations_readable: string;
  TotalCommitmentAmount: number | null;
  complete_data?: boolean | string | null;
  [key: string]: unknown;
};

// Helper function to clean region names by removing "BR:" and "CR:" prefixes
const cleanRegionName = (region: string): string => {
  return region.replace(/^(BR|CR):\s*/, "").trim();
};

const DENOMINATION_PALETTE = [
  "#7a7a7a",
  "#3ccccb",
  "#5ac189",
  "#454e7c",
  "#ff7f44",
  "#666666",
  "#a868b7",
  "#4a9fff",
  "#a38f79",
  "#f59e0b",
  "#64748b",
];

// Color map for denominations to ensure consistent colors
const DENOMINATION_COLOR_MAP: Record<string, string> = {
  USD: "#a868b7",
  EUR: "#5ac189",
  KWD: "#5ac189",
  SAR: "#454e7c",
  SDR: "#ff7f44",
  UA: "#666666",
  "USD and EUR": "#31a8a7",
  "USD and RMB": "#4a9fff",
  YEN: "#a38f79",
  "Denomination Missing": "#7a7a7a",
};

const INSTRUMENT_PALETTE = [
  "#565988",
  "#de7d47",
  "#c0415c",
  "#7cc286",
  "#9ca3af",
];

const CREDITOR_TYPE_PALETTE = [
  "#285A48", // banks
  "#466ed4",
  "#7ea6df",
  "#1d0a75", // banks, multi
  "#1f2e29", // bilateral
  "#408A71", // biggest
  "#573a53", // cap, private bond
  "#4f86df", // cred type missing
  "#305ac3",
  "#84a8dc",
];

const CREDITOR_REGION_ORDER = [
  "CR: Creditor Geographic Region Missing",
  "CR: Asia and Pacific",
  "CR: North America",
  "CR: Europe",
  "CR: Latin America and Caribbean",
  "CR: Africa",
  "CR: Middle East",
  "CR: Eurasia",
];

const CREDITOR_REGION_PALETTE = [
  "#466ed4",
  "#a63829", // africa
  "#eb9d0e", // latin
  "#1e945b", // eur
  "#707070", // missing region
  "#d9e4f4",
  "#b7c8e1",
];

const BORROWER_DEBT_DISTRESS_ORDER = [
  "Low Risk",
  "Moderate Risk",
  "<NULL>",
  "High Risk",
  "In Distress",
];

const BORROWER_DEBT_DISTRESS_PALETTE = [
  "#505686", // low risk
  "#eb9d0e", // moderate
  "#6d6d6d", // null
  "#cf4058", // high
  "#68b67a",
];

const DEBT_TO_GDP_ORDER = [
  "25-50%",
  "50-75%",
  ">100%",
  "<25%",
  "75-100%",
  "<NULL>",
];

const DEBT_TO_GDP_PALETTE = [
  "#6d6d6d",
  "#6db986",
  "#edc40e",
  "#cf4058",
  "#e57b3d",
  "#505686",
];

const BORROWER_REGION_ORDER = [
  "BR: Asia and Pacific",
  "BR: Africa",
  "BR: Latin America and Caribbean",
  "BR: Europe",
  "BR: Middle East",
  "BR: Eurasia",
  "BR: Borrower Geographic Region Missing",
];

const BORROWER_REGION_PALETTE = [
  "#466ed4",
  "#a63829", // africa
  "#eb9d0e", // latin
  "#1e945b", // eur
  "#707070", // missing region
  "#d9e4f4",
  "#b7c8e1",
];

const GOVERNING_LAW_ORDER = [
  "NY",
  "Other/Not specified",
  "England and Wales",
  "<NULL>",
  "England and Wales, Other/Not specified",
  "NY, Other/Not specified",
  "Borrower",
  "Borrower, Other/Not specified",
  "Other",
  "NY, Borrower",
];

const GOVERNING_LAW_PALETTE = [
  "#f2c200",
  "#ac9a82",
  "#fa7c43",
  "#6b6b6b",
  "#de4253",
  "#45bcbc",
  "#2a6fdb",
  "#8b8b8b",
  "#7f9dd4",
  "#c8b79d",
];

const GTC_ORDER = [
  "Not a General Terms & Conditions document",
  "General Terms & Conditions",
];

const GTC_PALETTE = ["#729e79", "#484d82", "#9ca3af"];

export default function AnalyticsDashboard() {
  const [activeTab, setActiveTab] = useState(0)
  const isMobile = useMediaQuery("(max-width:400px)");

  useEffect(() => {
    const tab = new URLSearchParams(window.location.search).get("tab")

    if(tab === "terms") {
      setActiveTab(1)
    } else if (tab === "provenance") {
      setActiveTab(2)
    } else {
      setActiveTab(0)
    }
  }, [])
  const [expandedLoanSections, setExpandedLoanSections] =
    useState<ExpandedLoanSections>({
      denomination: true,
      instrument: false,
      governingLaw: false,
      gtc: false,
      borrowerRegion: false,
    });

  const [filters, setFilters] = useState<AnalyticsFilters>({
    selectedCreditorRegions: [],
    selectedBorrowerRegions: [],
    selectedCreditorTypes: [],
    selectedDenominations: [],
    yearRange: [minYear, maxYear],
    completeDataOnly: false,
  });

  const creditorRegionOptions = useMemo(() => {
    const options = new Set<string>();
    flowRows.forEach((row) => {
      [row.source, row.target].forEach((node) => {
        const parsed = parseNode(node);
        if (parsed.kind === "creditorRegion") {
          options.add(parsed.value);
        }
      });
    });
    return Array.from(options).sort((a, b) => a.localeCompare(b));
  }, []);

  // Extract borrower regions from temporal_data for Denomination section
  const borrowerRegionOptions = useMemo(() => {
    const options = new Set<string>();
    const temporalRows = temporalDataJson.data as TemporalDataRow[];
    temporalRows.forEach((row) => {
      const cleanedRegion = cleanRegionName(row.BorrowerGeographicRegion);
      if (cleanedRegion && !cleanedRegion.includes("Missing")) {
        options.add(cleanedRegion);
      }
    });
    return Array.from(options).sort((a, b) => a.localeCompare(b));
  }, []);

  const filteredYears = useMemo(() => {
    return allYears.filter(
      (year) => year >= filters.yearRange[0] && year <= filters.yearRange[1],
    );
  }, [filters.yearRange]);

  const filteredFlowRows = useMemo(() => {
    const nodePassesCompleteData = (node: string) => {
      const parsed = parseNode(node);
      const isMissing =
        parsed.value.includes("Missing") || node.includes("Missing");

      if (filters.completeDataOnly && isMissing) {
        return false;
      }
      return true;
    };

    const rowPassesCategoryFilters = (row: {
      CreditorGeographicRegion?: string | null;
      BorrowerGeographicRegion?: string | null;
      pCreditorType?: string | null;
      inferred_denominations_readable?: string | null;
    }) => {
      if (filters.selectedCreditorRegions.length > 0) {
        const creditorRegion = cleanRegionName(
          String(row.CreditorGeographicRegion ?? ""),
        );
        if (!filters.selectedCreditorRegions.includes(creditorRegion)) {
          return false;
        }
      }

      if (filters.selectedBorrowerRegions.length > 0) {
        const borrowerRegion = cleanRegionName(
          String(row.BorrowerGeographicRegion ?? ""),
        );
        if (!filters.selectedBorrowerRegions.includes(borrowerRegion)) {
          return false;
        }
      }

      if (filters.selectedCreditorTypes.length > 0) {
        const creditorType = String(row.pCreditorType ?? "");
        if (!filters.selectedCreditorTypes.includes(creditorType)) {
          return false;
        }
      }

      if (filters.selectedDenominations.length > 0) {
        const denomination = String(row.inferred_denominations_readable ?? "");
        if (!filters.selectedDenominations.includes(denomination)) {
          return false;
        }
      }

      return true;
    };

    const aggregatedRows = new Map<string, number>();

    flowRows.forEach((row) => {
      const amount = Number(row["SUM(TotalCommitmentAmount)"] ?? 0);
      const rowYear = Number(row.DocumentYear);
      const yearPasses =
        !Number.isFinite(rowYear) ||
        (rowYear >= filters.yearRange[0] && rowYear <= filters.yearRange[1]);

      if (
        amount <= 0 ||
        !yearPasses ||
        !nodePassesCompleteData(row.source) ||
        !nodePassesCompleteData(row.target) ||
        !rowPassesCategoryFilters(row)
      ) {
        return;
      }

      if (filters.completeDataOnly && isCompleteDataFalse(row.complete_data)) {
        return;
      }

      const key = `${row.source}__@@__${row.target}`;
      aggregatedRows.set(key, (aggregatedRows.get(key) ?? 0) + amount);
    });

    return Array.from(aggregatedRows.entries()).map(([key, amount]) => {
      const [source, target] = key.split("__@@__");
      return {
        source,
        target,
        "SUM(TotalCommitmentAmount)": amount,
      };
    });
  }, [filters]);

  // Extract creditor types from temporal_data for Denomination section
  const denominationCreditorTypeColumns = useMemo(() => {
    const options = new Set<string>();
    const temporalRows = temporalDataJson.data as TemporalDataRow[];
    temporalRows.forEach((row) => {
      if (row.pCreditorType && !row.pCreditorType.includes("Missing")) {
        options.add(row.pCreditorType);
      }
    });
    return Array.from(options).sort((a, b) => a.localeCompare(b));
  }, []);

  // Filter temporal_data by selected borrower regions and creditor types
  const filteredTemporalDataForDenomination = useMemo(() => {
    const temporalRows = temporalDataJson.data as TemporalDataRow[];
    return temporalRows.filter((row) => {
      // Skip rows with missing denomination
      if (
        !row.inferred_denominations_readable ||
        row.inferred_denominations_readable.includes("Missing")
      ) {
        return false;
      }

      // Filter by borrower region if selected
      if (filters.selectedBorrowerRegions.length > 0) {
        const cleanedRegion = cleanRegionName(row.BorrowerGeographicRegion);
        if (!filters.selectedBorrowerRegions.includes(cleanedRegion)) {
          return false;
        }
      }

      // Filter by creditor type if selected
      if (filters.selectedCreditorTypes.length > 0) {
        if (!filters.selectedCreditorTypes.includes(row.pCreditorType)) {
          return false;
        }
      }

      return true;
    });
  }, [filters.selectedBorrowerRegions, filters.selectedCreditorTypes]);

  const filteredTemporalDataForLoanCharacteristics = useMemo(() => {
    const temporalRows = temporalDataJson.data as TemporalDataRow[];
    return temporalRows.filter((row) => {
      if (filters.selectedBorrowerRegions.length > 0) {
        const cleanedRegion = cleanRegionName(row.BorrowerGeographicRegion);
        if (!filters.selectedBorrowerRegions.includes(cleanedRegion)) {
          return false;
        }
      }

      if (filters.selectedCreditorTypes.length > 0) {
        if (!filters.selectedCreditorTypes.includes(row.pCreditorType)) {
          return false;
        }
      }

      return true;
    });
  }, [filters.selectedBorrowerRegions, filters.selectedCreditorTypes]);

  const hasLoanCharacteristicSubFilters =
    filters.selectedBorrowerRegions.length > 0 ||
    filters.selectedCreditorTypes.length > 0;

  const instrumentBaselineByYear = useMemo(() => {
    const map: Record<number, Record<string, number>> = {};

    instrumentYearRows.forEach((row) => {
      const year = Math.round(Number(row.DocumentYear));
      const yearData: Record<string, number> = {};

      instrumentColumns.forEach((column) => {
        yearData[column] = Number(row[column] ?? 0);
      });

      map[year] = yearData;
    });

    return map;
  }, []);

  const governingLawBaselineByYear = useMemo(() => {
    const map: Record<number, Record<string, number>> = {};

    governingLawYearRows.forEach((row) => {
      const year = Math.round(Number(row.DocumentYear));
      const yearData: Record<string, number> = {};

      governingLawColumns.forEach((column) => {
        yearData[column] = Number(row[column] ?? 0);
      });

      map[year] = yearData;
    });

    return map;
  }, []);

  const gtcBaselineByYear = useMemo(() => {
    const map: Record<number, Record<string, number>> = {};

    gtcYearRows.forEach((row) => {
      const year = Math.round(Number(row.DocumentYear));
      const yearData: Record<string, number> = {};

      gtcColumns.forEach((column) => {
        yearData[column] = Number(row[column] ?? 0);
      });

      map[year] = yearData;
    });

    return map;
  }, []);

  const baselineUsdByYear = useMemo(() => {
    const map: Record<number, number> = {};
    denominationYearRows.forEach((row) => {
      const year = Math.round(Number(row.DocumentYear));
      map[year] = Number(row.USD ?? 0);
    });
    return map;
  }, []);

  // Aggregate filtered temporal_data by year and denomination
  const aggregatedDenominationYearRows = useMemo<YearlyRow[]>(() => {
    const data: Record<number, Record<string, number>> = {};
    const hasDenominationSubFilters =
      filters.selectedBorrowerRegions.length > 0 ||
      filters.selectedCreditorTypes.length > 0;

    filteredTemporalDataForDenomination.forEach((row) => {
      const year = Math.round(Number(row.DocumentYear));
      const denomination =
        row.inferred_denominations_readable || "Denomination Missing";
      // Use TotalCommitmentAmount if available and > 0, otherwise count as 1
      const amount =
        Number(row.TotalCommitmentAmount ?? 0) > 0
          ? Number(row.TotalCommitmentAmount)
          : 1;

      if (!data[year]) {
        data[year] = {};
      }

      if (!data[year][denomination]) {
        data[year][denomination] = 0;
      }

      data[year][denomination] += amount;
    });

    // Align default USD totals with the baseline denomination-year dataset.
    // This correction is only applied when denomination subfilters are not active.
    if (!hasDenominationSubFilters) {
      Object.entries(baselineUsdByYear).forEach(([yearKey, baselineUsd]) => {
        const year = Number(yearKey);
        const currentUsd = data[year]?.USD ?? 0;
        const delta = baselineUsd - currentUsd;

        if (delta > 0) {
          if (!data[year]) {
            data[year] = {};
          }
          data[year].USD = currentUsd + delta;
        }
      });
    }

    // Convert to the format expected by the charts
    return Object.entries(data)
      .map(([year, denominations]) => ({
        DocumentYear: Number(year),
        ...denominations,
      }))
      .sort((a, b) => a.DocumentYear - b.DocumentYear) as YearlyRow[];
  }, [
    baselineUsdByYear,
    filteredTemporalDataForDenomination,
    filters.selectedBorrowerRegions.length,
    filters.selectedCreditorTypes.length,
  ]);

  // Extract years from aggregated temporal data for Denomination section
  const denominationYears = useMemo(() => {
    const start = Math.max(minYear, filters.yearRange[0]);
    const end = Math.min(maxYear, filters.yearRange[1]);
    const length = Math.max(0, end - start + 1);

    return Array.from({ length }, (_, index) => start + index);
  }, [filters.yearRange]);

  // Extract denomination columns from aggregated data (all years, not just first)
  const selectedDenominationColumnsFromTemporal = useMemo(() => {
    if (aggregatedDenominationYearRows.length === 0) return [];

    // Collect all unique denomination columns from all years
    const columnSet = new Set<string>();
    aggregatedDenominationYearRows.forEach((row) => {
      Object.keys(row)
        .filter((col) => col !== "DocumentYear")
        .forEach((col) => columnSet.add(col));
    });

    const columns = Array.from(columnSet).sort();

    return filters.completeDataOnly
      ? columns.filter((col) => !col.includes("Missing"))
      : columns;
  }, [aggregatedDenominationYearRows, filters.completeDataOnly]);

  const selectedInstrumentColumns = useMemo(() => {
    return filters.completeDataOnly
      ? instrumentColumns.filter((column) => !column.includes("Missing"))
      : instrumentColumns;
  }, [filters.completeDataOnly]);

  const instrumentTemporalYearRows = useMemo<YearlyRow[]>(() => {
    const data: Record<number, Record<string, number>> = {};

    filteredTemporalDataForLoanCharacteristics.forEach((row) => {
      const year = Math.round(Number(row.DocumentYear));
      const label = (row.Instrument_Type as string | null) ?? "<NULL>";
      const amount = Number(row.TotalCommitmentAmount ?? 0);

      if (!Number.isFinite(amount) || amount <= 0) {
        return;
      }

      if (!data[year]) {
        data[year] = {};
      }

      data[year][label] = (data[year][label] ?? 0) + amount;
    });

    if (!hasLoanCharacteristicSubFilters) {
      Object.entries(instrumentBaselineByYear).forEach(
        ([yearKey, baselineByCategory]) => {
          const year = Number(yearKey);

          Object.entries(baselineByCategory).forEach(
            ([category, baselineValue]) => {
              const currentValue = data[year]?.[category] ?? 0;
              const delta = baselineValue - currentValue;

              if (delta > 0) {
                if (!data[year]) {
                  data[year] = {};
                }
                data[year][category] = currentValue + delta;
              }
            },
          );
        },
      );
    }

    return Object.entries(data)
      .map(([year, categories]) => ({
        DocumentYear: Number(year),
        ...categories,
      }))
      .sort((a, b) => a.DocumentYear - b.DocumentYear) as YearlyRow[];
  }, [
    filteredTemporalDataForLoanCharacteristics,
    hasLoanCharacteristicSubFilters,
    instrumentBaselineByYear,
  ]);

  const selectedCreditorTypeColumns = useMemo(() => {
    const columnSet = new Set<string>();

    filteredTemporalDataForLoanCharacteristics.forEach((row) => {
      const label =
        (row.pCreditorType as string | null) ?? "Creditor Type Missing";
      columnSet.add(label);
    });

    const chosen =
      filters.selectedCreditorTypes.length > 0
        ? filters.selectedCreditorTypes
        : Array.from(columnSet).sort((a, b) => a.localeCompare(b));

    return filters.completeDataOnly
      ? chosen.filter((column) => !column.includes("Missing"))
      : chosen;
  }, [
    filteredTemporalDataForLoanCharacteristics,
    filters.completeDataOnly,
    filters.selectedCreditorTypes,
  ]);

  const creditorTypeTemporalYearRows = useMemo<YearlyRow[]>(() => {
    const data: Record<number, Record<string, number>> = {};

    filteredTemporalDataForLoanCharacteristics.forEach((row) => {
      const year = Math.round(Number(row.DocumentYear));
      const label =
        (row.pCreditorType as string | null) ?? "Creditor Type Missing";
      const amount = Number(row.TotalCommitmentAmount ?? 0);

      if (!Number.isFinite(amount) || amount <= 0) {
        return;
      }

      if (!data[year]) {
        data[year] = {};
      }

      data[year][label] = (data[year][label] ?? 0) + amount;
    });

    return Object.entries(data)
      .map(([year, categories]) => ({
        DocumentYear: Number(year),
        ...categories,
      }))
      .sort((a, b) => a.DocumentYear - b.DocumentYear) as YearlyRow[];
  }, [filteredTemporalDataForLoanCharacteristics]);

  const selectedCreditorRegionColumns = useMemo(() => {
    const columnSet = new Set<string>();

    filteredTemporalDataForLoanCharacteristics.forEach((row) => {
      const label =
        (row.CreditorGeographicRegion as string | null) ??
        "CR: Creditor Geographic Region Missing";
      columnSet.add(label);
    });

    const baseColumns = Array.from(columnSet);
    const orderedColumns = CREDITOR_REGION_ORDER.filter((column) =>
      baseColumns.includes(column),
    );
    const extraColumns = baseColumns
      .filter((column) => !orderedColumns.includes(column))
      .sort((a, b) => a.localeCompare(b));
    const columns = [...orderedColumns, ...extraColumns];

    return filters.completeDataOnly
      ? columns.filter((column) => !column.includes("Missing"))
      : columns;
  }, [filteredTemporalDataForLoanCharacteristics, filters.completeDataOnly]);

  const creditorRegionTemporalYearRows = useMemo<YearlyRow[]>(() => {
    const data: Record<number, Record<string, number>> = {};

    filteredTemporalDataForLoanCharacteristics.forEach((row) => {
      const year = Math.round(Number(row.DocumentYear));
      const label =
        (row.CreditorGeographicRegion as string | null) ??
        "CR: Creditor Geographic Region Missing";
      const amount = Number(row.TotalCommitmentAmount ?? 0);

      if (!Number.isFinite(amount) || amount <= 0) {
        return;
      }

      if (!data[year]) {
        data[year] = {};
      }

      data[year][label] = (data[year][label] ?? 0) + amount;
    });

    return Object.entries(data)
      .map(([year, categories]) => ({
        DocumentYear: Number(year),
        ...categories,
      }))
      .sort((a, b) => a.DocumentYear - b.DocumentYear) as YearlyRow[];
  }, [filteredTemporalDataForLoanCharacteristics]);

  const selectedBorrowerDebtDistressColumns = useMemo(() => {
    const columnSet = new Set<string>();

    filteredTemporalDataForLoanCharacteristics.forEach((row) => {
      const label =
        (row.BorrowerDebtDistressLevel as string | null) ?? "<NULL>";
      columnSet.add(label);
    });

    const baseColumns = Array.from(columnSet);
    const orderedColumns = BORROWER_DEBT_DISTRESS_ORDER.filter((column) =>
      baseColumns.includes(column),
    );
    const extraColumns = baseColumns
      .filter((column) => !orderedColumns.includes(column))
      .sort((a, b) => a.localeCompare(b));
    const columns = [...orderedColumns, ...extraColumns];

    return filters.completeDataOnly
      ? columns.filter(
          (column) => column !== "<NULL>" && !column.includes("Missing"),
        )
      : columns;
  }, [filteredTemporalDataForLoanCharacteristics, filters.completeDataOnly]);

  const borrowerDebtDistressTemporalYearRows = useMemo<YearlyRow[]>(() => {
    const data: Record<number, Record<string, number>> = {};

    filteredTemporalDataForLoanCharacteristics.forEach((row) => {
      const year = Math.round(Number(row.DocumentYear));
      const label =
        (row.BorrowerDebtDistressLevel as string | null) ?? "<NULL>";
      const amount = Number(row.TotalCommitmentAmount ?? 0);

      if (!Number.isFinite(amount) || amount <= 0) {
        return;
      }

      if (!data[year]) {
        data[year] = {};
      }

      data[year][label] = (data[year][label] ?? 0) + amount;
    });

    return Object.entries(data)
      .map(([year, categories]) => ({
        DocumentYear: Number(year),
        ...categories,
      }))
      .sort((a, b) => a.DocumentYear - b.DocumentYear) as YearlyRow[];
  }, [filteredTemporalDataForLoanCharacteristics]);

  const selectedDebtToGdpColumns = useMemo(() => {
    const columnSet = new Set<string>();

    filteredTemporalDataForLoanCharacteristics.forEach((row) => {
      const label =
        (row.BorrowerDebttoGDPCategory as string | null) ?? "<NULL>";
      columnSet.add(label);
    });

    const baseColumns = Array.from(columnSet);
    const orderedColumns = DEBT_TO_GDP_ORDER.filter((column) =>
      baseColumns.includes(column),
    );
    const extraColumns = baseColumns
      .filter((column) => !orderedColumns.includes(column))
      .sort((a, b) => a.localeCompare(b));
    const columns = [...orderedColumns, ...extraColumns];

    return filters.completeDataOnly
      ? columns.filter(
          (column) => column !== "<NULL>" && !column.includes("Missing"),
        )
      : columns;
  }, [filteredTemporalDataForLoanCharacteristics, filters.completeDataOnly]);

  const debtToGdpTemporalYearRows = useMemo<YearlyRow[]>(() => {
    const data: Record<number, Record<string, number>> = {};

    filteredTemporalDataForLoanCharacteristics.forEach((row) => {
      const year = Math.round(Number(row.DocumentYear));
      const label =
        (row.BorrowerDebttoGDPCategory as string | null) ?? "<NULL>";
      const amount = Number(row.TotalCommitmentAmount ?? 0);

      if (!Number.isFinite(amount) || amount <= 0) {
        return;
      }

      if (!data[year]) {
        data[year] = {};
      }

      data[year][label] = (data[year][label] ?? 0) + amount;
    });

    return Object.entries(data)
      .map(([year, categories]) => ({
        DocumentYear: Number(year),
        ...categories,
      }))
      .sort((a, b) => a.DocumentYear - b.DocumentYear) as YearlyRow[];
  }, [filteredTemporalDataForLoanCharacteristics]);

  const selectedBorrowerRegionColumns = useMemo(() => {
    const columnSet = new Set<string>();

    filteredTemporalDataForLoanCharacteristics.forEach((row) => {
      const label =
        (row.BorrowerGeographicRegion as string | null) ??
        "BR: Borrower Geographic Region Missing";
      columnSet.add(label);
    });

    const baseColumns = Array.from(columnSet);
    const orderedColumns = BORROWER_REGION_ORDER.filter((column) =>
      baseColumns.includes(column),
    );
    const extraColumns = baseColumns
      .filter((column) => !orderedColumns.includes(column))
      .sort((a, b) => a.localeCompare(b));
    const columns = [...orderedColumns, ...extraColumns];

    return filters.completeDataOnly
      ? columns.filter((column) => !column.includes("Missing"))
      : columns;
  }, [filteredTemporalDataForLoanCharacteristics, filters.completeDataOnly]);

  const borrowerRegionTemporalYearRows = useMemo<YearlyRow[]>(() => {
    const data: Record<number, Record<string, number>> = {};

    filteredTemporalDataForLoanCharacteristics.forEach((row) => {
      const year = Math.round(Number(row.DocumentYear));
      const label =
        (row.BorrowerGeographicRegion as string | null) ??
        "BR: Borrower Geographic Region Missing";
      const amount = Number(row.TotalCommitmentAmount ?? 0);

      if (!Number.isFinite(amount) || amount <= 0) {
        return;
      }

      if (!data[year]) {
        data[year] = {};
      }

      data[year][label] = (data[year][label] ?? 0) + amount;
    });

    return Object.entries(data)
      .map(([year, categories]) => ({
        DocumentYear: Number(year),
        ...categories,
      }))
      .sort((a, b) => a.DocumentYear - b.DocumentYear) as YearlyRow[];
  }, [filteredTemporalDataForLoanCharacteristics]);

  const selectedGoverningLawColumns = useMemo(() => {
    const baseColumns = filters.completeDataOnly
      ? governingLawColumns.filter((column) => !column.includes("Missing"))
      : governingLawColumns;

    const orderedColumns = GOVERNING_LAW_ORDER.filter((column) =>
      baseColumns.includes(column),
    );
    const extraColumns = baseColumns.filter(
      (column) => !orderedColumns.includes(column),
    );

    return [...orderedColumns, ...extraColumns];
  }, [filters.completeDataOnly]);

  const selectedGtcColumns = useMemo(() => {
    const baseColumns = gtcColumns.filter(
      (column) => column !== "<NULL>" && !column.includes("Missing"),
    );

    const orderedColumns = GTC_ORDER.filter((column) =>
      baseColumns.includes(column),
    );
    const extraColumns = baseColumns.filter(
      (column) => !orderedColumns.includes(column),
    );

    return [...orderedColumns, ...extraColumns];
  }, []);

  const governingLawTemporalYearRows = useMemo<YearlyRow[]>(() => {
    const data: Record<number, Record<string, number>> = {};

    filteredTemporalDataForLoanCharacteristics.forEach((row) => {
      const year = Math.round(Number(row.DocumentYear));
      const label = (row.GoverningLaw as string | null) ?? "<NULL>";
      const amount = Number(row.TotalCommitmentAmount ?? 0);

      if (!Number.isFinite(amount) || amount <= 0) {
        return;
      }

      if (!data[year]) {
        data[year] = {};
      }

      data[year][label] = (data[year][label] ?? 0) + amount;
    });

    if (!hasLoanCharacteristicSubFilters) {
      Object.entries(governingLawBaselineByYear).forEach(
        ([yearKey, baselineByCategory]) => {
          const year = Number(yearKey);

          Object.entries(baselineByCategory).forEach(
            ([category, baselineValue]) => {
              const currentValue = data[year]?.[category] ?? 0;
              const delta = baselineValue - currentValue;

              if (delta > 0) {
                if (!data[year]) {
                  data[year] = {};
                }
                data[year][category] = currentValue + delta;
              }
            },
          );
        },
      );
    }

    return Object.entries(data)
      .map(([year, categories]) => ({
        DocumentYear: Number(year),
        ...categories,
      }))
      .sort((a, b) => a.DocumentYear - b.DocumentYear) as YearlyRow[];
  }, [
    filteredTemporalDataForLoanCharacteristics,
    governingLawBaselineByYear,
    hasLoanCharacteristicSubFilters,
  ]);

  const gtcTemporalYearRows = useMemo<YearlyRow[]>(() => {
    const data: Record<number, Record<string, number>> = {};

    filteredTemporalDataForLoanCharacteristics.forEach((row) => {
      const year = Math.round(Number(row.DocumentYear));
      const label = (row.gtc as string | null) ?? "<NULL>";
      const amount = Number(row.TotalCommitmentAmount ?? 0);

      if (!Number.isFinite(amount) || amount <= 0) {
        return;
      }

      if (!data[year]) {
        data[year] = {};
      }

      data[year][label] = (data[year][label] ?? 0) + amount;
    });

    if (!hasLoanCharacteristicSubFilters) {
      Object.entries(gtcBaselineByYear).forEach(
        ([yearKey, baselineByCategory]) => {
          const year = Number(yearKey);

          Object.entries(baselineByCategory).forEach(
            ([category, baselineValue]) => {
              const currentValue = data[year]?.[category] ?? 0;
              const delta = baselineValue - currentValue;

              if (delta > 0) {
                if (!data[year]) {
                  data[year] = {};
                }
                data[year][category] = currentValue + delta;
              }
            },
          );
        },
      );
    }

    return Object.entries(data)
      .map(([year, categories]) => ({
        DocumentYear: Number(year),
        ...categories,
      }))
      .sort((a, b) => a.DocumentYear - b.DocumentYear) as YearlyRow[];
  }, [
    filteredTemporalDataForLoanCharacteristics,
    gtcBaselineByYear,
    hasLoanCharacteristicSubFilters,
  ]);

  const analyticsTheme = useMemo(
    () =>
      createTheme({
        typography: {
          fontFamily: '"Inter", sans-serif',
        },
      }),
    [],
  );

  // User Stats data for 4 metric boxes
  const { metricCards: USERS_METRIC } = useUserStatsData();

  return (
    <ThemeProvider theme={analyticsTheme}>
      <Box sx={{ bgcolor: "#eef2f6", pb: 5 }}>
        {/* Blue banner */}
        <Box
          sx={{
            bgcolor: "#041e42",
            color: "#fff",
            px: { xs: 2, md: 4 },
            py: 4,
            display: "flex",
            justifyContent: "start",
          }}
        >
          <Box sx={{ paddingLeft: "1rem" }}>
            <Typography
              component="h1"
              sx={{
                fontSize: { xs: "1rem", md: "1.4rem" },
                fontWeight: 800,
                lineHeight: 1.1,
              }}
            >
              <Box component="span" sx={{ color: "#00d4ff" }}>
                #PublicDebtIsPublic
              </Box>{" "}
              Analytics
            </Typography>
            <Typography
              sx={{
                mt: "0.1rem",
                fontSize: { xs: "0.8rem", md: "1rem" },
                color: "#d7e7ff",
              }}
            >
              Global Sovereign Debt Document Metrics and Insights
            </Typography>
          </Box>
        </Box>

        {/* Metrics cards – always visible above tabs */}
        <Box
          sx={{
            px: { xs: 2, md: 4 },
            pt: 4,
            pb: 0,
            maxWidth: 1400,
            mx: "auto",
          }}
        >
          <AnalyticsMetricCards
            activeTab={activeTab}
            userMetrics={USERS_METRIC}
          />
        </Box>

        {/* Tab bar */}
        <Box
          sx={{
            bgcolor: "#fff",
            mt: 3,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Box sx={{ px: { xs: 2, md: 4 }, maxWidth: 1400, mx: "auto" }}>
            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v as number)}
              orientation={isMobile ? "vertical" : "horizontal"}
              variant={isMobile ? "fullWidth" : "standard"}
              sx={{
                "& .MuiTab-root.Mui-selected": {
                  backgroundColor: isMobile
                    ? "rgba(25, 118, 210, 0.08)"
                    : "transparent",
                  borderRadius: isMobile ? 1 : 0,
                },
              }}
            >
              {/* Temporarily hide the first 3 tabs. CreditFlows is available as an internal page */}
              <Tab label="Term Coverage" />
              <Tab label="Terms by Instrument and Creditor Type" />
              <Tab label="Provenance" />
              {/* <Tab label="User Statistics" /> */}
              {/* <Tab label="Credit Flows" />
              <Tab label="Loan Characteristics" />
              <Tab label="Party Characteristics" /> */}
            </Tabs>
          </Box>
        </Box>

        {/* Tab panels */}
        <Box sx={{ px: { xs: 2, md: 4 }, py: 4, maxWidth: 1400, mx: "auto" }}>
          {activeTab === 0 && <ClauseTab clauseRows={clauseRows} />}

          {activeTab === 1 && <ClauseDeepDiveTab clauseRows={clauseRows} />}

          {activeTab === 2 && <ProvenanceTab />}

          {activeTab === 3 && (
            <CreditFlowsTab
              filteredFlowRows={filteredFlowRows}
              filters={filters}
              setFilters={setFilters}
              creditorRegionOptions={creditorRegionOptions}
              borrowerRegionOptions={borrowerRegionOptions}
              creditorTypeColumns={creditorTypeColumns}
              denominationColumns={denominationColumns}
              minYear={minYear}
              maxYear={maxYear}
            />
          )}

          {activeTab === 4 && (
            <LoanCharacteristicsTab
              expandedLoanSections={expandedLoanSections}
              setExpandedLoanSections={setExpandedLoanSections}
              filters={filters}
              setFilters={setFilters}
              creditorRegionOptions={creditorRegionOptions}
              borrowerRegionOptions={borrowerRegionOptions}
              denominationCreditorTypeColumns={denominationCreditorTypeColumns}
              denominationColumns={denominationColumns}
              minYear={minYear}
              maxYear={maxYear}
              aggregatedDenominationYearRows={aggregatedDenominationYearRows}
              denominationYears={denominationYears}
              selectedDenominationColumnsFromTemporal={
                selectedDenominationColumnsFromTemporal
              }
              denominationPalette={DENOMINATION_PALETTE}
              denominationColorMap={DENOMINATION_COLOR_MAP}
              instrumentTemporalYearRows={instrumentTemporalYearRows}
              filteredYears={filteredYears}
              selectedInstrumentColumns={selectedInstrumentColumns}
              instrumentPalette={INSTRUMENT_PALETTE}
              governingLawTemporalYearRows={governingLawTemporalYearRows}
              selectedGoverningLawColumns={selectedGoverningLawColumns}
              governingLawPalette={GOVERNING_LAW_PALETTE}
              gtcTemporalYearRows={gtcTemporalYearRows}
              selectedGtcColumns={selectedGtcColumns}
              gtcPalette={GTC_PALETTE}
            />
          )}
          {activeTab === 5 && (
            <PartyCharacteristicsTab
              expandedLoanSections={expandedLoanSections}
              setExpandedLoanSections={setExpandedLoanSections}
              filters={filters}
              setFilters={setFilters}
              creditorRegionOptions={creditorRegionOptions}
              borrowerRegionOptions={borrowerRegionOptions}
              denominationCreditorTypeColumns={denominationCreditorTypeColumns}
              denominationColumns={denominationColumns}
              minYear={minYear}
              maxYear={maxYear}
              creditorTypeTemporalYearRows={creditorTypeTemporalYearRows}
              selectedCreditorTypeColumns={selectedCreditorTypeColumns}
              creditorTypePalette={CREDITOR_TYPE_PALETTE}
              creditorRegionTemporalYearRows={creditorRegionTemporalYearRows}
              filteredYears={filteredYears}
              selectedCreditorRegionColumns={selectedCreditorRegionColumns}
              creditorRegionPalette={CREDITOR_REGION_PALETTE}
              borrowerDebtDistressTemporalYearRows={
                borrowerDebtDistressTemporalYearRows
              }
              selectedBorrowerDebtDistressColumns={
                selectedBorrowerDebtDistressColumns
              }
              borrowerDebtDistressPalette={BORROWER_DEBT_DISTRESS_PALETTE}
              debtToGdpTemporalYearRows={debtToGdpTemporalYearRows}
              selectedDebtToGdpColumns={selectedDebtToGdpColumns}
              debtToGdpPalette={DEBT_TO_GDP_PALETTE}
              borrowerRegionTemporalYearRows={borrowerRegionTemporalYearRows}
              selectedBorrowerRegionColumns={selectedBorrowerRegionColumns}
              borrowerRegionPalette={BORROWER_REGION_PALETTE}
            />
          )}

          {activeTab === 6 && <UserStatisticsTab />}
        </Box>
      </Box>
    </ThemeProvider>
  );
}
